import { type FC } from "react";

import { useMutation } from "@apollo/client/react";

import { faBoxArchive } from "@fortawesome/pro-duotone-svg-icons/faBoxArchive";
import { faBoxOpen } from "@fortawesome/pro-duotone-svg-icons/faBoxOpen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { type ProjectFragment, ProjectUnarchiveDocument } from "@app_schema";

import { useVisible } from "@application/hooks/use_visible";

import { Menu } from "@styled/menu";
import { MenuButtonEllipsis } from "@styled/menu_button_ellipsis";
import { MenuItemButton } from "@styled/menu_item_button";
import { MenuItemButtonDestroy } from "@styled/menu_item_button_destroy";
import { MenuItemLinkEdit } from "@styled/menu_item_link_modify";
import { MenuItems } from "@styled/menu_items";
import { MenuSeparator } from "@styled/menu_separator";

import { ProjectArchiveDialog } from "./project_archive_dialog";
import { ProjectDestroyDialog } from "./project_destroy_dialog";

type ProjectMenuProject = Pick<
  ProjectFragment,
  "id" | "name" | "archived" | "archivedAt"
> & {
  policy: Pick<ProjectFragment["policy"], "destroy" | "archive">;
};

export const ProjectMenu: FC<{
  project: ProjectMenuProject;
  onDestroy?(): void;
  onArchive?(): void;
  onUnarchive?(): void;
  showEdit?: boolean;
}> = ({ project, onDestroy, onArchive, onUnarchive, showEdit = true }) => {
  const [destroying, onDestroying] = useVisible();
  const [archiving, onArchiving] = useVisible();
  // Refetch by query name catches every active `DashboardProjectsList`
  // regardless of tab/page variables — Apollo updates the project's own
  // `archived` field but won't recompute the paginated `entries` array.
  const [unarchive, { loading: unarchiving }] = useMutation(
    ProjectUnarchiveDocument,
    {
      refetchQueries: ["DashboardProjectsList", "InboxStatusCounts"],
      awaitRefetchQueries: true,
    },
  );

  const canArchive = project.policy.archive && !project.archived;
  const canUnarchive = project.policy.archive && project.archived;
  const hasItems =
    showEdit || project.policy.destroy || canArchive || canUnarchive;

  if (!hasItems) return null;

  const handleUnarchive = async () => {
    await unarchive({ variables: { id: project.id } });
    onUnarchive?.();
  };

  return (
    <>
      {destroying && (
        <ProjectDestroyDialog
          project={project}
          onClose={onDestroying}
          onDestroy={onDestroy}
        />
      )}
      {archiving && (
        <ProjectArchiveDialog
          project={project}
          onClose={onArchiving}
          onArchive={onArchive}
        />
      )}

      <Menu>
        <MenuButtonEllipsis />
        <MenuItems anchor="bottom end">
          {showEdit && (
            <MenuItemLinkEdit to={`/dashboard/projects/${project.id}/edit`} />
          )}
          {canArchive && (
            <MenuItemButton onClick={onArchiving}>
              <FontAwesomeIcon icon={faBoxArchive} /> Archive
            </MenuItemButton>
          )}
          {canUnarchive && (
            <MenuItemButton onClick={handleUnarchive} loading={unarchiving}>
              <FontAwesomeIcon icon={faBoxOpen} /> Unarchive
            </MenuItemButton>
          )}
          {project.policy.destroy && (
            <>
              {(showEdit || canArchive || canUnarchive) && <MenuSeparator />}
              <MenuItemButtonDestroy onClick={onDestroying} />
            </>
          )}
        </MenuItems>
      </Menu>
    </>
  );
};
